import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const PriceContext = createContext();

export const usePrices = () => {
  const context = useContext(PriceContext);
  if (!context) {
    throw new Error('usePrices must be used within a PriceProvider');
  }
  return context;
};

export const PriceProvider = ({ children }) => {
  const [prices, setPrices] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);

  // Unified symbol mapping for all trading pages
  const symbolMapping = {
    // Crypto symbols (used in Contract Trading) - now using USDT as primary
    'BTCUSDT': 'BTC',
    'ETHUSDT': 'ETH', 
    'ADAUSDT': 'ADA',
    'SOLUSDT': 'SOL',
    'MATICUSDT': 'MATIC',
    'BNBUSDT': 'BNB',
    'XRPUSDT': 'XRP',
    'DOGEUSDT': 'DOGE',
    'LTCUSDT': 'LTC',
    'LINKUSDT': 'LINK',
    'AVAXUSDT': 'AVAX',
    'ATOMUSDT': 'ATOM',
    'UNIUSDT': 'UNI',
    
    // TradFi symbols (used in TradFi page)
    'EURUSD': 'EURUSD',
    'GBPUSD': 'GBPUSD',
    'USDJPY': 'USDJPY',
    'USDCHF': 'USDCHF',
    'AUDUSD': 'AUDUSD',
    'USDCAD': 'USDCAD',
    'NZDUSD': 'NZDUSD',
    'EURGBP': 'EURGBP',
    'EURJPY': 'EURJPY',
    'GBPJPY': 'GBPJPY',
    
    // Commodities
    'XAUUSD': 'XAUUSD',
    'XAGUSD': 'XAGUSD',
    'USOIL': 'USOIL',
    'UKOIL': 'UKOIL',
    'NATGAS': 'NATGAS',
    
    // Indices
    'SPX500': 'SPX500',
    'NAS100': 'NAS100',
    'UK100': 'UK100',
    'GER30': 'GER30',
    'FRA40': 'FRA40',
    'JPN225': 'JPN225'
  };

  // Base prices for fallback simulation
  const basePrices = {
    'BTCUSDT': 45000.00, 'ETHUSDT': 2800.00, 'ADAUSDT': 0.45, 'SOLUSDT': 100.00,
    'MATICUSDT': 0.85, 'BNBUSDT': 300.00, 'XRPUSDT': 0.65, 'DOGEUSDT': 0.08, 'LTCUSDT': 75.00,
    'LINKUSDT': 15.50, 'AVAXUSDT': 25.00, 'ATOMUSDT': 8.50, 'UNIUSDT': 6.20,
    'EURUSD': 1.0850, 'GBPUSD': 1.2650, 'USDJPY': 149.50, 'USDCHF': 0.8750,
    'AUDUSD': 0.6550, 'USDCAD': 1.3650, 'NZDUSD': 0.6050, 'EURGBP': 0.8580,
    'EURJPY': 162.20, 'GBPJPY': 189.20,
    'XAUUSD': 2020.00, 'XAGUSD': 24.50, 'USOIL': 75.50, 'UKOIL': 80.20, 'NATGAS': 2.85,
    'SPX500': 4500.00, 'NAS100': 15000.00, 'UK100': 7500.00, 'GER30': 16000.00,
    'FRA40': 7200.00, 'JPN225': 33000.00
  };

  // Volatility for price simulation
  const volatility = {
    'BTCUSDT': 100.0, 'ETHUSDT': 50.0, 'ADAUSDT': 0.01, 'SOLUSDT': 5.0,
    'MATICUSDT': 0.02, 'BNBUSDT': 10.0, 'XRPUSDT': 0.01, 'DOGEUSDT': 0.005, 'LTCUSDT': 5.0,
    'LINKUSDT': 0.5, 'AVAXUSDT': 1.0, 'ATOMUSDT': 0.3, 'UNIUSDT': 0.2,
    'EURUSD': 0.0001, 'GBPUSD': 0.0002, 'USDJPY': 0.01, 'USDCHF': 0.0002,
    'AUDUSD': 0.0002, 'USDCAD': 0.0002, 'NZDUSD': 0.0003, 'EURGBP': 0.0002,
    'EURJPY': 0.02, 'GBPJPY': 0.03,
    'XAUUSD': 2.0, 'XAGUSD': 0.2, 'USOIL': 0.5, 'UKOIL': 0.5, 'NATGAS': 0.1,
    'SPX500': 5.0, 'NAS100': 10.0, 'UK100': 10.0, 'GER30': 10.0,
    'FRA40': 10.0, 'JPN225': 50.0
  };

  // Balance state — updated via WebSocket balanceUpdate messages
  const [balance, setBalance] = useState(null);
  const wsRef = useRef(null);

  // Connect via WebSocket — receives both priceUpdate and balanceUpdate from the server
  const connectWebSocket = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setConnectionStatus('Connected to Bybit');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'priceUpdate' && msg.data) {
          // Merge incoming prices — never wipe existing ones
          // (Bybit WS sends one symbol at a time; full snapshot on connect)
          const updates = {};
          Object.entries(msg.data).forEach(([symbol, price]) => {
            if (!price || !isFinite(price)) return;
            const entry = {
              price,
              bid: price * 0.9995,
              ask: price * 1.0005,
              change: 0,
              changePercent: 0,
              timestamp: msg.timestamp,
              source: 'Bybit'
            };
            updates[symbol] = entry;
            const mapped = symbolMapping[symbol];
            if (mapped) updates[mapped] = entry;
          });
          setPrices(prev => ({ ...prev, ...updates }));
          setLastUpdate(msg.timestamp);
        } else if (msg.type === 'balanceUpdate' && msg.data) {
          setBalance(msg.data);
        } else if (msg.type === 'tradeClosed' || msg.type === 'reconcile') {
          // Only reload if the server initiated the close (SL/TP auto-close or reconcile).
          // User-initiated closes (dashboard Close button) are already handled in the frontend
          // and must NOT trigger a reload — that was causing the "bot turns off" glitch.
          if (msg.serverInitiated !== false) {
            console.log('🔄 Server-initiated trade close — reloading to sync state...', msg);
            window.location.reload();
          } else {
            console.log('ℹ️  User-initiated close confirmed by server (no reload needed)', msg);
          }
        }
      } catch (e) { /* ignore */ }
    };

    ws.onclose = () => {
      setIsConnected(false);
      setConnectionStatus('Reconnecting...');
      setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  };

  // Fallback: also check status via REST on mount
  const connectToBybit = async () => {
    try {
      const res = await fetch('/api/bybit/status');
      const data = await res.json();
      if (data.connected) {
        setIsConnected(true);
        setConnectionStatus('Connected to Bybit');
      }
    } catch (e) { /* ignore — WS will set status */ }
    connectWebSocket();
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // Start simulated price feed
  const startSimulatedPriceFeed = () => {
    const generatePrices = () => {
      const newPrices = {};
      
      Object.entries(basePrices).forEach(([symbol, basePrice]) => {
        const vol = volatility[symbol] || 0.01;
        const change = (Math.random() - 0.5) * vol;
        const newPrice = basePrice + change;
        
        newPrices[symbol] = {
          price: newPrice,
          bid: newPrice - (vol * 0.1),
          ask: newPrice + (vol * 0.1),
          change: change,
          changePercent: (change / basePrice) * 100,
          timestamp: new Date().toISOString(),
          source: 'Simulated'
        };
      });
      
      setPrices(newPrices);
      setLastUpdate(new Date().toISOString());
    };

    // Update every 1 second for simulation
    const interval = setInterval(generatePrices, 1000);
    generatePrices(); // Initial generation
    
    return () => clearInterval(interval);
  };

  // Generate simulated prices (fallback)
  const generateSimulatedPrices = () => {
    const newPrices = {};
    
    Object.entries(basePrices).forEach(([symbol, basePrice]) => {
      const vol = volatility[symbol] || 0.01;
      const change = (Math.random() - 0.5) * vol;
      const newPrice = basePrice + change;
      
      newPrices[symbol] = {
        price: newPrice,
        bid: newPrice - (vol * 0.1),
        ask: newPrice + (vol * 0.1),
        change: change,
        changePercent: (change / basePrice) * 100,
        timestamp: new Date().toISOString(),
        source: 'Simulated'
      };
      
      // Also create mapped symbol for trading pages
      const mappedSymbol = symbolMapping[symbol];
      if (mappedSymbol) {
        newPrices[mappedSymbol] = newPrices[symbol];
      }
    });
    
    setPrices(newPrices);
    setLastUpdate(new Date().toISOString());
  };

  // Get price for a symbol (with fallback)
  const getPrice = (symbol) => {
    // Try direct match first
    if (prices[symbol]) {
      return prices[symbol];
    }
    
    // Try symbol mapping
    const mappedSymbol = symbolMapping[symbol];
    if (mappedSymbol && prices[mappedSymbol]) {
      return prices[mappedSymbol];
    }
    
    // Fallback to base price
    const basePrice = basePrices[symbol] || basePrices[mappedSymbol] || 1.0;
    return {
      price: basePrice,
      bid: basePrice * 0.999,
      ask: basePrice * 1.001,
      change: 0,
      changePercent: 0,
      timestamp: new Date().toISOString(),
      source: 'Fallback'
    };
  };

  // Get all available symbols
  const getAvailableSymbols = () => {
    return Object.keys(symbolMapping);
  };

  // Auto-connect on mount
  useEffect(() => {
    connectToBybit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    prices,
    isConnected,
    connectionStatus,
    lastUpdate,
    balance,           // live balance from balanceUpdate WS message
    getPrice,
    getAvailableSymbols,
    connectToBybit,
    symbolMapping
  };

  return (
    <PriceContext.Provider value={value}>
      {children}
    </PriceContext.Provider>
  );
};
